import React, {useEffect, useState} from 'react';
import {
    Form,
    Radio,
    Select,
    Button,
    Table,
    Tag,
    Input,
    notification, Popconfirm
} from 'antd';
import dayjs from 'dayjs';
import api from "../../config/axiosConfig";
import {DeleteOutlined} from "@ant-design/icons";

// Helper to generate next 12 months
const MONTH_OPTIONS = Array.from({ length: 12 }).map((_, i) => {
    const d = dayjs().add(i, 'month');
    const key = d.format('YYYY-MM');
    return {
        label: d.format('MMMM YYYY'),
        value: key,
    };
});


export default function DemoFeeSuspensionPage() {
    // local state for "saved" suspension rules
    const [suspensions, setSuspensions] = useState([]);

    // Table columns exactly like your real list
    const columns = [
        {
            title: 'თარიღი',
            dataIndex: 'dates',
            render: (months) => months.map(m => <Tag key={m}>{m}</Tag>),
        },
        {
            title: 'ტიპი',
            dataIndex: 'type',
            render: cat =>
                cat === 'ALL' ? 'ყველა'
                    : cat === 'ONLY' ? 'მხოლოდ'
                        : 'ყველა, გარდა...',
        },
        {
            title: 'გუნდი',
            dataIndex: 'teams',
            render: (teamIds, record) => {
                // If type is ALL, show "ALL"
                if (record.type === 'ALL') {
                    return '-';
                }

                // If type is ONLY or ALL_EXCEPT, show team IDs
                if (record.type === 'ONLY' || record.type === 'ALL_EXCEPT') {
                    if (!teamIds || teamIds.length === 0) return '-';

                    // Handle both single ID and array of IDs
                    const idsArray = Array.isArray(teamIds) ? teamIds : [teamIds];

                    return idsArray.map(teamId => {
                        const team = teams?.find(t => t.id === teamId);
                        return team ? <Tag key={teamId}>{team.name}</Tag> : <Tag key={teamId}>Unknown Team</Tag>;
                    });
                }

                return '-';
            }
        },
        {
            title: 'აღწერა',
            dataIndex: 'description',
            render: desc => desc || '-'
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (text, record) => (
                <>
                    <Popconfirm
                        title="Are you sure you want to delete this suspension?"
                        onConfirm={() => handleDeleteSuspension(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button type="link" danger>
                            <DeleteOutlined/>
                        </Button>
                    </Popconfirm>
                </>
            ),
        },
    ];

    const [form] = Form.useForm();
    const [type, setType] = useState('ALL');
    const [teams, setTeams] = useState([]);

    const suspensionsList = async () => {
        try {
            const response = await api.get('settings/holiday_config');
            setSuspensions(response.data);
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to fetch suspensions list.',
                placement: 'topRight',
            });
        }
    }

    const teamsList = async () => {
        try {
            const response = await api.get('teams');
            setTeams(response.data);
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to fetch teams list.',
                placement: 'topRight',
            });
        }
    }

    const onFinish = async (values) => {
        try {
            await api.post('settings/holiday_config', values);
            notification.success({
                message: 'Success',
                description: 'Suspension added successfully',
                placement: 'topRight'
            });
            form.resetFields(['teams', 'dates', 'description']);
            setType('all');
            suspensionsList()
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to save suspension. Please try again.',
                placement: 'topRight'
            });
        }
    };

    const handleDeleteSuspension = async (id) => {
        try {
            await api.delete(`settings/holiday_config/${id}`);
            notification.success({
                message: 'Success',
                description: 'Suspension deleted successfully',
                placement: 'topRight'
            });
            suspensionsList(); // Refresh the list after deletion
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to delete suspension. Please try again.',
                placement: 'topRight'
            });
        }
    }

    useEffect(() => {
        teamsList();
        suspensionsList()
    }, []);

    return (
        <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '2rem',
            padding: '1rem'
        }}>
            <div style={{ flex: 1, maxWidth: 400 }}>
                <h3>⚙️ გადასახადის დარიცხვის შეჩერება</h3>
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{ type: 'all' }}
                    onFinish={onFinish}
                >
                    <Form.Item name="type" label="აირჩიე კატეგორია">
                        <Radio.Group onChange={e => setType(e.target.value)}>
                            <Radio.Button value="ALL">ყველა</Radio.Button>
                            <Radio.Button value="ONLY">ზოგიერთი</Radio.Button>
                            <Radio.Button value="ALL_EXCEPT">ყველა, გარდა...</Radio.Button>
                        </Radio.Group>
                    </Form.Item>

                    {(type === 'ONLY' || type === 'ALL_EXCEPT') && (
                        <Form.Item
                            name="teams"
                            label={'გუნდი'}
                            rules={[{ required: true, message: 'Please pick at least one team' }]}
                        >
                            <Select
                                mode="multiple"
                                placeholder="აირჩიე გუნდი"
                                options={teams?.map(t => ({ label: t.name, value: t.id }))}
                            />
                        </Form.Item>
                    )}

                    <Form.Item
                        name="dates"
                        label="თარიღი"
                        rules={[{ required: true, message: 'Pick at least one month' }]}
                    >
                        <Select
                            mode="multiple"
                            placeholder="აირჩიეთ თარიღი"
                            options={MONTH_OPTIONS}
                        />
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label="აღწერა"
                    >
                        <Input.TextArea
                            placeholder="ჩაწერეთ გადასახადის გაუქმების მიზეზი"
                            rows={3}
                            maxLength={500}
                            showCount
                        />
                    </Form.Item>

                    <Form.Item>
                        <Button type="primary" htmlType="submit">
                            შენახვა
                        </Button>
                    </Form.Item>
                </Form>
            </div>
            <div style={{ flex: 1 }}>
                <h3>📋 შეჩერებული დარიცხვები</h3>
                <Table
                    rowKey="id"
                    dataSource={suspensions}
                    columns={columns}
                    pagination={false}
                    locale={{ emptyText: 'No suspensions yet' }}
                />
            </div>
        </div>
    );
}